import { uploadMedia } from '@/features/atendimento/api';
import { AudioPreview } from '@/features/atendimento/components/AudioPreview';
import { AudioRecorder } from '@/features/atendimento/components/AudioRecorder';
import { EmojiPicker } from '@/features/atendimento/components/EmojiPicker';
import { useQuickReplies } from '@/features/atendimento/hooks';
import { applySignature, useAssinaturaTravada } from '@/features/atendimento/lib/assinatura';
import { compressImage } from '@/features/atendimento/lib/compress-image';
import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { usePosicaoFlutuante } from '@/shared/hooks/usePosicaoFlutuante';
import { ApiError } from '@/shared/lib/api-client';
/**
 * Modal "Agendar mensagem" — abre pelo ícone do composer (canais QR Code) e,
 * em modo edição, pela página Mensagens Agendadas.
 *
 * Mensagem nova: variáveis do contato ({{firstname}} etc.) e a assinatura do
 * atendente são aplicadas AO SALVAR, como o composer faz ao enviar — o texto
 * guardado é o que vai sair. Na edição o texto já está pronto (pode ser de
 * outro atendente): nada é reaplicado.
 */
import { renderContactVars } from '@/shared/lib/contact-vars';
import { useAuthStore } from '@/shared/stores/auth-store';
import {
  CalendarClock,
  FileText,
  Image as ImageIcon,
  Loader2,
  Mic,
  Music,
  Paperclip,
  Search,
  Smile,
  UserCheck,
  UserX,
  Video,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import {
  type ScheduledMessage,
  type ScheduledType,
  createScheduledMessage,
  updateScheduledMessage,
  useInvalidarAgendamentos,
} from '../api';
import {
  LIMITE_POR_TIPO,
  combinarDataHora,
  formatarQuando,
  paraCampos,
  problemaNoHorario,
  proximaHoraCheia,
  tipoDoArquivo,
} from '../lib';

export interface ContatoDoAgendamento {
  displayName: string | null;
  phone: string | null;
  email?: string | null;
  stateUf?: string | null;
  customFields?: Record<string, unknown> | null;
}

export type ContextoDoAgendamento =
  | { tipo: 'novo'; ticketId: number; contato: ContatoDoAgendamento }
  | { tipo: 'editar'; agendamento: ScheduledMessage };

interface Anexo {
  mediaId: number;
  nome: string;
  tipo: Exclude<ScheduledType, 'text'>;
  /** Áudio gravado ou escolhido agora: URL local pra ouvir antes de agendar. */
  previewUrl?: string;
  durationSec?: number;
}

const ICONE_DO_TIPO = { image: ImageIcon, audio: Music, video: Video, document: FileText };

export function ScheduleMessageModal({
  contexto,
  onClose,
}: {
  /** null = fechado. */
  contexto: ContextoDoAgendamento | null;
  onClose: () => void;
}) {
  const open = contexto !== null;
  const userName = useAuthStore((s) => s.user?.name);
  // Assinatura: ligada por padrão no agendamento (decisão do dono), desligável
  // por mensagem — sem mexer na preferência do composer. Travada pelo gestor:
  // sempre ligada.
  const assinaturaTravada = useAssinaturaTravada();
  const [assinar, setAssinar] = useState(true);
  const invalidar = useInvalidarAgendamentos();
  const quickRepliesQ = useQuickReplies();

  const [texto, setTexto] = useState('');
  const [anexo, setAnexo] = useState<Anexo | null>(null);
  const [abrirAtendimento, setAbrirAtendimento] = useState(false);
  const [data, setData] = useState('');
  const [hora, setHora] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviandoArquivo, setEnviandoArquivo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [emojiAberto, setEmojiAberto] = useState(false);
  const [rapidasAbertas, setRapidasAbertas] = useState(false);
  const [buscaRapida, setBuscaRapida] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiBtnRef = useRef<HTMLButtonElement>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);
  const rapidasRef = useRef<HTMLDivElement>(null);
  // A lista de respostas rápidas é desenhada no body (portal), presa ao botão:
  // dentro do corpo do modal, que rola, ela saía cortada — igual ao emoji.
  const rapidasBtnRef = useRef<HTMLButtonElement>(null);
  const rapidasPainelRef = useRef<HTMLDivElement>(null);
  const buscaRapidaRef = useRef<HTMLInputElement>(null);
  const posicaoDasRapidas = usePosicaoFlutuante(rapidasBtnRef, rapidasAbertas, {
    largura: 320,
    altura: 280,
    lado: 'bottom',
  });

  // Preenche SÓ ao abrir (ou trocar de agendamento) — não a cada render: a
  // conversa re-renderiza com o tempo real e a página refaz a lista a cada
  // 30 s, recriando o objeto `contexto`; reagir à identidade dele apagaria o
  // que o atendente está digitando. Por isso a dependência é a chave.
  const chave = contexto
    ? contexto.tipo === 'novo'
      ? `novo:${contexto.ticketId}`
      : `editar:${contexto.agendamento.id}`
    : null;
  const contextoRef = useRef(contexto);
  contextoRef.current = contexto;
  // Prévia de áudio da vez anterior: a URL local é liberada ao abrir de novo.
  const anexoRef = useRef(anexo);
  anexoRef.current = anexo;
  useEffect(() => {
    const contexto = contextoRef.current;
    if (!chave || !contexto) return;
    if (anexoRef.current?.previewUrl) URL.revokeObjectURL(anexoRef.current.previewUrl);
    setErro(null);
    setGravando(false);
    setEmojiAberto(false);
    setRapidasAbertas(false);
    setBuscaRapida('');
    if (contexto.tipo === 'novo') {
      setTexto('');
      setAnexo(null);
      setAbrirAtendimento(false);
      setAssinar(true);
      const campos = paraCampos(proximaHoraCheia(new Date()));
      setData(campos.data);
      setHora(campos.hora);
    } else {
      const a = contexto.agendamento;
      setTexto(a.body ?? '');
      setAnexo(
        a.media && a.message_type !== 'text'
          ? { mediaId: a.media.id, nome: a.media.name ?? 'arquivo', tipo: a.message_type }
          : null,
      );
      setAbrirAtendimento(a.open_ticket);
      const campos = paraCampos(new Date(a.send_at));
      setData(campos.data);
      setHora(campos.hora);
    }
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [chave]);

  // Fecha a lista de respostas rápidas ao clicar fora dela.
  useEffect(() => {
    if (!rapidasAbertas) return;
    const fora = (e: MouseEvent): void => {
      const alvo = e.target as Node;
      if (rapidasRef.current?.contains(alvo) || rapidasPainelRef.current?.contains(alvo)) return;
      setRapidasAbertas(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [rapidasAbertas]);

  // Cursor na busca ao abrir a lista. Não dá pra usar `autoFocus`: o painel
  // nasce invisível até medir a posição, e o navegador não foca o que está
  // invisível — foca no quadro seguinte, já visível (mesmo jeito do emoji).
  useEffect(() => {
    if (!rapidasAbertas) return;
    const quadro = requestAnimationFrame(() => buscaRapidaRef.current?.focus());
    return () => cancelAnimationFrame(quadro);
  }, [rapidasAbertas]);

  useEscapeClose(open && !emojiAberto && !rapidasAbertas, onClose);

  const renderVars = (t: string): string => {
    if (!contexto) return t;
    const c: ContatoDoAgendamento =
      contexto.tipo === 'novo'
        ? contexto.contato
        : {
            displayName: contexto.agendamento.contact.name,
            phone: contexto.agendamento.contact.phone,
          };
    return renderContactVars(t, {
      contact: {
        displayName: c.displayName,
        phone: c.phone,
        email: c.email ?? null,
        stateUf: c.stateUf ?? null,
        customFields: c.customFields ?? null,
      },
      userName,
    });
  };

  const rapidasFiltradas = useMemo(() => {
    const termo = buscaRapida.trim().toLowerCase();
    return (quickRepliesQ.data ?? [])
      .filter(
        (q) =>
          !termo ||
          q.shortcut.toLowerCase().includes(termo) ||
          q.body.toLowerCase().includes(termo),
      )
      .slice(0, 50);
  }, [quickRepliesQ.data, buscaRapida]);

  if (!contexto) return null;

  const ehAudio = anexo?.tipo === 'audio';

  const inserir = (trecho: string): void => {
    const ta = textareaRef.current;
    const ini = ta?.selectionStart ?? texto.length;
    const fim = ta?.selectionEnd ?? texto.length;
    const proximo = texto.slice(0, ini) + trecho + texto.slice(fim);
    setTexto(proximo);
    requestAnimationFrame(() => {
      ta?.focus();
      ta?.setSelectionRange(ini + trecho.length, ini + trecho.length);
    });
  };

  const escolherArquivo = async (file: File): Promise<void> => {
    setErro(null);
    const tipo = tipoDoArquivo(file.type || 'application/octet-stream');
    let blob: Blob = file;
    let nome = file.name;
    let dims: { width: number; height: number } | null = null;
    if (tipo === 'image') {
      // Mesma compressão do composer (JPEG, até 1920px) — cabe no limite.
      try {
        const c = await compressImage(file, { forceJpeg: true });
        if (c.blob !== file) {
          blob = c.blob;
          if (c.blob.type === 'image/jpeg' && !/\.jpe?g$/i.test(nome)) {
            nome = `${nome.replace(/\.[^.]+$/, '')}.jpg`;
          }
        }
        if (c.width && c.height) dims = { width: c.width, height: c.height };
      } catch {
        // Falhou comprimir: segue com o original (o limite abaixo decide).
      }
    }
    if (blob.size > LIMITE_POR_TIPO[tipo]) {
      setErro(`Arquivo excede o limite (${(LIMITE_POR_TIPO[tipo] / 1024 / 1024).toFixed(0)} MB).`);
      return;
    }
    setEnviandoArquivo(true);
    try {
      const r = await uploadMedia(blob, nome, dims ?? undefined);
      trocarAnexo({
        mediaId: r.media_id,
        nome: r.filename ?? nome,
        tipo,
        ...(tipo === 'audio' && { previewUrl: URL.createObjectURL(blob) }),
      });
    } catch (err) {
      setErro(err instanceof ApiError ? err.problem.title : 'Não foi possível enviar o arquivo.');
    } finally {
      setEnviandoArquivo(false);
    }
  };

  /** Troca o anexo liberando a URL local do anterior (prévia de áudio). */
  const trocarAnexo = (novo: Anexo | null): void => {
    if (anexo?.previewUrl) URL.revokeObjectURL(anexo.previewUrl);
    setAnexo(novo);
  };

  // Gravação: mesmo caminho do composer — o gravador entrega ogg/opus com
  // duração e forma de onda; sobe e vira o anexo (áudio, sem texto junto).
  const gravacaoConcluida = async (
    blob: Blob,
    durationSec: number,
    waveform: number[],
  ): Promise<void> => {
    setGravando(false);
    setErro(null);
    const ogg = new Blob([blob], { type: 'audio/ogg' });
    if (ogg.size > LIMITE_POR_TIPO.audio) {
      setErro('Áudio excede o limite (16 MB).');
      return;
    }
    setEnviandoArquivo(true);
    try {
      const nome = `audio-${Date.now()}.ogg`;
      const r = await uploadMedia(ogg, nome, { durationSec, waveform });
      trocarAnexo({
        mediaId: r.media_id,
        nome: 'Áudio gravado',
        tipo: 'audio',
        previewUrl: URL.createObjectURL(ogg),
        durationSec,
      });
    } catch (err) {
      setErro(err instanceof ApiError ? err.problem.title : 'Não foi possível enviar o áudio.');
    } finally {
      setEnviandoArquivo(false);
    }
  };

  const salvar = async (): Promise<void> => {
    if (salvando || enviandoArquivo || gravando) return;
    const quando = combinarDataHora(data, hora);
    const problema = problemaNoHorario(quando, new Date());
    const cru = ehAudio ? '' : texto.trim();
    if (!anexo && !cru) {
      setErro('Escreva a mensagem ou anexe um arquivo.');
      return;
    }
    if (problema || !quando) {
      setErro(problema);
      return;
    }
    // Nova: variáveis + assinatura agora (o salvo é o que vai sair). Edição:
    // texto como está.
    const corpo =
      cru && contexto.tipo === 'novo'
        ? applySignature(renderVars(cru), userName, assinar || assinaturaTravada)
        : cru;
    const payload = {
      message_type: anexo ? anexo.tipo : ('text' as const),
      body: corpo || null,
      media_id: anexo?.mediaId ?? null,
      send_at: quando.toISOString(),
      open_ticket: abrirAtendimento,
    };
    setSalvando(true);
    setErro(null);
    try {
      if (contexto.tipo === 'novo') {
        await createScheduledMessage({ ticket_id: contexto.ticketId, ...payload });
      } else {
        await updateScheduledMessage(contexto.agendamento.id, payload);
      }
      await invalidar();
      toast.success(contexto.tipo === 'novo' ? 'Mensagem agendada' : 'Agendamento atualizado', {
        description: formatarQuando(payload.send_at, new Date()),
      });
      onClose();
    } catch (err) {
      setErro(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title)
          : 'Não foi possível salvar o agendamento.',
      );
    } finally {
      setSalvando(false);
    }
  };

  const IconeAnexo = anexo ? ICONE_DO_TIPO[anexo.tipo] : FileText;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={contexto.tipo === 'novo' ? 'Agendar mensagem' : 'Editar agendamento'}
    >
      <div className="flex max-h-[92vh] w-[520px] max-w-full flex-col rounded-lg border border-n-200 bg-n-0 shadow-lg">
        {/* Cabeçalho */}
        <div className="flex items-start gap-3 border-n-200 border-b px-5 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-pill bg-brand-50 text-brand-600">
            <CalendarClock size={20} strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-bold text-[16px] text-n-900">
              {contexto.tipo === 'novo' ? 'Agendar mensagem' : 'Editar agendamento'}
            </h2>
            <p className="text-[12px] text-n-500">
              {contexto.tipo === 'novo'
                ? 'Programe uma mensagem para ser enviada depois'
                : `Para ${contexto.agendamento.contact.name ?? contexto.agendamento.contact.phone ?? 'o contato'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
          {/* Mensagem */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="agendar-texto" className="font-semibold text-[12px] text-n-700">
              Mensagem
            </label>
            <textarea
              id="agendar-texto"
              ref={textareaRef}
              value={ehAudio ? '' : texto}
              onChange={(e) => setTexto(e.target.value)}
              disabled={ehAudio}
              rows={4}
              maxLength={anexo ? 1024 : 4096}
              placeholder={
                ehAudio
                  ? 'Áudio vai sem texto junto.'
                  : anexo
                    ? 'Texto junto do arquivo (opcional)'
                    : 'Digite a mensagem'
              }
              className="w-full resize-none rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none placeholder:text-n-400 focus-visible:outline-none disabled:bg-n-50"
            />
            <div className="flex items-center gap-1">
              <div className="relative">
                <button
                  ref={emojiBtnRef}
                  type="button"
                  aria-label="Emoji"
                  title="Emoji"
                  disabled={ehAudio}
                  onClick={() => setEmojiAberto((v) => !v)}
                  className="flex h-[32px] w-[32px] items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-n-700 disabled:opacity-40"
                >
                  <Smile size={17} strokeWidth={1.75} />
                </button>
                <EmojiPicker
                  open={emojiAberto}
                  onClose={() => setEmojiAberto(false)}
                  onSelect={inserir}
                  anchorRef={emojiBtnRef}
                  placement="bottom"
                  flutuante
                />
              </div>
              <div className="relative" ref={rapidasRef}>
                <button
                  ref={rapidasBtnRef}
                  type="button"
                  aria-label="Mensagens rápidas"
                  title="Mensagens rápidas"
                  disabled={ehAudio}
                  onClick={() => setRapidasAbertas((v) => !v)}
                  className="flex h-[32px] w-[32px] items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-n-700 disabled:opacity-40"
                >
                  <Zap size={17} strokeWidth={1.75} />
                </button>
                {rapidasAbertas &&
                  createPortal(
                    <div
                      ref={rapidasPainelRef}
                      className="fixed z-[60] flex max-h-[280px] w-[320px] flex-col overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-md"
                      style={
                        posicaoDasRapidas
                          ? {
                              top: posicaoDasRapidas.top,
                              left: posicaoDasRapidas.left,
                              maxHeight: posicaoDasRapidas.altura,
                            }
                          : { top: 0, left: 0, visibility: 'hidden' }
                      }
                    >
                      {/* Busca no mesmo formato da do seletor de emoji. */}
                      <div className="flex-shrink-0 border-n-200 border-b px-2 py-1.5">
                        <div className="relative">
                          <Search
                            size={14}
                            strokeWidth={1.75}
                            className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 text-n-400"
                            aria-hidden="true"
                          />
                          <input
                            ref={buscaRapidaRef}
                            value={buscaRapida}
                            onChange={(e) => setBuscaRapida(e.target.value)}
                            placeholder="Buscar mensagem rápida"
                            aria-label="Buscar mensagem rápida"
                            className="h-[32px] w-full rounded-md bg-n-100 pr-3 pl-8 text-[13px] text-n-900 outline-none placeholder:text-n-400 focus-visible:outline-none"
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto py-1">
                        {rapidasFiltradas.length === 0 ? (
                          <div className="px-3 py-3 text-[12px] text-n-500">
                            Nenhuma mensagem rápida.
                          </div>
                        ) : (
                          rapidasFiltradas.map((q) => (
                            <button
                              key={q.id}
                              type="button"
                              onClick={() => {
                                inserir(renderVars(q.body));
                                setRapidasAbertas(false);
                              }}
                              className="flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left hover:bg-n-50"
                            >
                              <span className="font-semibold text-[12px] text-brand-600">
                                {q.shortcut}
                              </span>
                              <span className="line-clamp-2 text-[12px] text-n-600">{q.body}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>,
                    document.body,
                  )}
              </div>
              <button
                type="button"
                aria-label="Anexar arquivo"
                title="Anexar arquivo"
                disabled={enviandoArquivo}
                onClick={() => arquivoRef.current?.click()}
                className="flex h-[32px] w-[32px] items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-n-700 disabled:opacity-40"
              >
                {enviandoArquivo ? (
                  <Loader2 size={17} strokeWidth={1.75} className="animate-spin" />
                ) : (
                  <Paperclip size={17} strokeWidth={1.75} />
                )}
              </button>
              <input
                ref={arquivoRef}
                type="file"
                className="hidden"
                accept="image/*,audio/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void escolherArquivo(f);
                }}
              />
              <button
                type="button"
                aria-label="Gravar áudio"
                title={anexo ? 'Remova o arquivo para gravar um áudio' : 'Gravar áudio'}
                disabled={enviandoArquivo || gravando || anexo !== null}
                onClick={() => {
                  setErro(null);
                  setGravando(true);
                }}
                className="flex h-[32px] w-[32px] items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-n-700 disabled:opacity-40"
              >
                <Mic size={17} strokeWidth={1.75} />
              </button>
              {contexto.tipo === 'novo' && (
                <button
                  type="button"
                  aria-pressed={assinar || assinaturaTravada}
                  aria-label={
                    assinaturaTravada
                      ? 'Essa função foi desabilitada pelo gestor.'
                      : assinar
                        ? 'Assinatura ativada'
                        : 'Assinatura desativada'
                  }
                  title={
                    assinaturaTravada
                      ? 'Essa função foi desabilitada pelo gestor.'
                      : assinar
                        ? 'Assinatura ativada'
                        : 'Assinatura desativada'
                  }
                  disabled={ehAudio}
                  onClick={() => {
                    if (!assinaturaTravada) setAssinar((v) => !v);
                  }}
                  className={`flex h-[32px] w-[32px] items-center justify-center rounded-md transition disabled:opacity-40 ${
                    assinar || assinaturaTravada
                      ? 'bg-brand-50 text-brand-600 hover:bg-brand-100'
                      : 'text-n-500 hover:bg-n-100 hover:text-n-700'
                  } ${assinaturaTravada ? 'cursor-not-allowed' : ''}`}
                >
                  {assinar || assinaturaTravada ? (
                    <UserCheck size={17} strokeWidth={1.75} />
                  ) : (
                    <UserX size={17} strokeWidth={1.75} />
                  )}
                </button>
              )}
            </div>
            {gravando && (
              <div className="flex items-center gap-1.5">
                <AudioRecorder onCancel={() => setGravando(false)} onComplete={gravacaoConcluida} />
              </div>
            )}
            {anexo?.previewUrl && anexo.tipo === 'audio' && (
              <div className="flex items-center">
                <AudioPreview
                  src={anexo.previewUrl}
                  estimatedDuration={anexo.durationSec ?? 0}
                  onDiscard={() => trocarAnexo(null)}
                />
              </div>
            )}
            {anexo && !(anexo.previewUrl && anexo.tipo === 'audio') && (
              <div className="flex items-center gap-2 rounded-md border border-n-200 bg-n-50 px-3 py-2">
                <IconeAnexo size={16} strokeWidth={1.75} className="flex-shrink-0 text-n-500" />
                <span className="min-w-0 flex-1 truncate text-[12px] text-n-700">{anexo.nome}</span>
                <button
                  type="button"
                  onClick={() => trocarAnexo(null)}
                  aria-label="Remover arquivo"
                  className="flex h-6 w-6 items-center justify-center rounded-md text-n-500 hover:bg-n-200"
                >
                  <X size={13} />
                </button>
              </div>
            )}
          </div>

          {/* Reabrir */}
          <div className="flex items-start justify-between gap-3 rounded-md border border-n-200 px-3 py-2.5">
            <div>
              <div className="font-semibold text-[13px] text-n-800">
                Abrir atendimento ao enviar
              </div>
              <div className="text-[11px] text-n-500">
                Se o cliente não tiver conversa aberta, abre um novo atendimento: nos seus Ativos,
                se você estiver online, ou em Pendentes, se estiver offline.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={abrirAtendimento}
              aria-label="Abrir atendimento ao enviar"
              onClick={() => setAbrirAtendimento((v) => !v)}
              className={`relative mt-0.5 inline-flex h-[22px] w-[40px] flex-shrink-0 items-center rounded-pill transition ${
                abrirAtendimento ? 'bg-brand-500' : 'bg-n-200'
              }`}
            >
              <span
                className={`absolute h-[18px] w-[18px] rounded-pill bg-n-0 shadow-sm transition ${
                  abrirAtendimento ? 'translate-x-[20px]' : 'translate-x-[2px]'
                }`}
              />
            </button>
          </div>

          {/* Quando enviar */}
          <div className="flex flex-col gap-1.5">
            <span className="font-semibold text-[12px] text-n-700">Quando enviar</span>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-n-500">Data</span>
                <input
                  type="date"
                  value={data}
                  min={paraCampos(new Date()).data}
                  onChange={(e) => setData(e.target.value)}
                  className="h-[36px] rounded-md border border-n-200 bg-n-0 px-3 text-[13px] text-n-900 outline-none focus:border-brand-500"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-n-500">Hora</span>
                <input
                  type="time"
                  value={hora}
                  onChange={(e) => setHora(e.target.value)}
                  className="h-[36px] rounded-md border border-n-200 bg-n-0 px-3 text-[13px] text-n-900 outline-none focus:border-brand-500"
                />
              </label>
            </div>
          </div>

          {erro && <div className="text-[12px] text-danger-600">{erro}</div>}
        </div>

        {/* Rodapé */}
        <div className="flex justify-end gap-2 border-n-200 border-t px-5 py-3">
          <Button variant="outline" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={() => void salvar()} disabled={salvando || enviandoArquivo || gravando}>
            {salvando ? <Loader2 className="animate-spin" /> : <CalendarClock />}
            {contexto.tipo === 'novo' ? 'Agendar' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
